/**
 * Import a recipe — DESIGN_GUIDELINES.md §9.2.
 *
 * Import is ASYNCHRONOUS by design: the API only enqueues, and a Docker worker does the fetching.
 * So this screen must be honest about the queue. "Queued" is a legitimate visible state, not a
 * hung spinner, and a durable queue means closing the tab does not lose the import.
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, Anchor, Button, Card, Group, List, Loader, Stack, Text, TextInput, ThemeIcon, Title } from '@mantine/core';
import { PageShell } from '../components/PageShell';
import { ApiError, fetchJob, importRecipe, retryJob, type ImportJob, type SessionUser } from '../lib/api';
import { usePolling } from '../lib/useAsync';

/** Plain-language stage labels (§12 — never expose internals to users). */
const STAGE_LABELS: Record<string, string> = {
  queued: 'Waiting to import…',
  fetch: 'Fetching the recipe…',
  parse: 'Reading the recipe…',
  assets: 'Downloading the images…',
  persist: 'Saving…',
  done: 'Done',
};

const STAGE_ORDER = ['fetch', 'parse', 'assets', 'persist'] as const;

function statusLabel(job: ImportJob): string {
  if (job.status === 'queued') return STAGE_LABELS.queued!;
  if (job.status === 'failed') return 'Import failed';
  if (job.status === 'done') return STAGE_LABELS.done!;
  return STAGE_LABELS[job.stage ?? 'fetch'] ?? 'Working…';
}

export function ImportPage({
  user,
  onSignOut,
}: {
  user: SessionUser;
  onSignOut: () => void;
}) {
  const navigate = useNavigate();
  const [url, setUrl] = useState('');
  const [jobId, setJobId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [retrying, setRetrying] = useState(false);

  const job = usePolling<ImportJob>(
    () => fetchJob(jobId!),
    (value) => value.status === 'done' || value.status === 'failed',
    2000,
    jobId !== null,
  );

  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault();
    setSubmitting(true);
    setError(null);
    setJobId(null);

    try {
      const result = await importRecipe(url.trim());
      if (result.status === 'already_imported') {
        // The recipe is shared globally, so a second user importing the same link is instant.
        navigate(`/recipes/${result.recipeId}`);
        return;
      }
      setJobId(result.jobId);
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Could not start the import');
    } finally {
      setSubmitting(false);
    }
  }

  async function handleRetry() {
    if (!jobId) return;
    setRetrying(true);
    try {
      await retryJob(jobId);
      job.data && (job.data.status = 'queued');
      setJobId(null);
      // Re-arm polling against the same job.
      setTimeout(() => setJobId(jobId), 0);
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Could not retry');
    } finally {
      setRetrying(false);
    }
  }

  const active = job.data;
  const currentStageIndex = active?.stage ? STAGE_ORDER.indexOf(active.stage as never) : -1;

  return (
    <PageShell user={user} onSignOut={onSignOut} title="Import a recipe">
      <form onSubmit={handleSubmit}>
        <Stack gap="md" maw={640}>
          <TextInput
            label="HelloFresh recipe URL"
            placeholder="https://www.hellofresh.co.uk/recipes/..."
            value={url}
            onChange={(event) => setUrl(event.currentTarget.value)}
            required
            size="md"
          />
          <Group>
            <Button type="submit" loading={submitting} radius="xl" size="md" disabled={url.trim() === ''}>
              Import recipe
            </Button>
          </Group>
        </Stack>
      </form>

      {error ? (
        <Alert color="red" variant="light" mt="lg" title="Import failed" role="alert">
          {error}
        </Alert>
      ) : null}

      {active ? (
        <Card withBorder radius="lg" padding="lg" mt="lg" maw={640}>
          <Stack gap="sm">
            <Group gap="sm">
              {active.status === 'processing' || active.status === 'queued' ? <Loader size="sm" /> : null}
              <Title order={3}>{statusLabel(active)}</Title>
            </Group>

            {/* Stage checklist. Completed stages tick, the active one is emphasised. */}
            <List spacing={4} size="sm" center>
              {STAGE_ORDER.map((stage, index) => {
                const done = active.status === 'done' || index < currentStageIndex;
                const isCurrent = active.status === 'processing' && index === currentStageIndex;
                return (
                  <List.Item
                    key={stage}
                    icon={
                      <ThemeIcon
                        size={18}
                        radius="xl"
                        variant={done ? 'filled' : 'light'}
                        color={done ? 'hfGreen' : 'gray'}
                      >
                        <span aria-hidden="true" style={{ fontSize: 10 }}>
                          {done ? '✓' : isCurrent ? '•' : ''}
                        </span>
                      </ThemeIcon>
                    }
                    style={{
                      color: done
                        ? 'var(--ff-green-700)'
                        : isCurrent
                          ? 'var(--ff-ink)'
                          : 'var(--ff-muted)',
                      fontWeight: isCurrent ? 600 : 400,
                    }}
                  >
                    {STAGE_LABELS[stage]}
                  </List.Item>
                );
              })}
            </List>

            {active.status === 'queued' ? (
              <Text size="sm" c="var(--ff-muted)">
                Waiting behind other imports. This is saved — you can close the tab and it will
                still finish.
              </Text>
            ) : null}

            {active.status === 'failed' ? (
              <>
                <Alert color="red" variant="light" title="Could not import this recipe">
                  {active.error ?? 'Unknown error'}
                </Alert>
                <Group>
                  <Button onClick={handleRetry} loading={retrying} variant="light" radius="xl">
                    Try again
                  </Button>
                </Group>
              </>
            ) : null}

            {active.status === 'done' && active.recipeId ? (
              <Group>
                <Button onClick={() => navigate(`/recipes/${active.recipeId}`)} radius="xl">
                  View recipe
                </Button>
                {active.parserTier !== null && active.parserTier > 1 ? (
                  <Text size="sm" c="var(--ff-amber-600)">
                    Imported with reduced detail — serving sizes unavailable.
                  </Text>
                ) : null}
              </Group>
            ) : null}
          </Stack>
        </Card>
      ) : null}

      <Text size="sm" c="var(--ff-muted)" mt="xl" maw={640}>
        Only HelloFresh recipe pages can be imported. Once a recipe is imported the planner keeps
        its own copy of the ingredients, instructions and photos.
      </Text>

      <Anchor href="https://www.hellofresh.co.uk/recipes" target="_blank" rel="noreferrer" size="sm" mt="xs" display="block">
        Find recipes on HelloFresh
      </Anchor>
    </PageShell>
  );
}
